/**
 * Discount math shared between quotes and invoices.
 *
 * A discount is described by { discount_type, discount_value, discount_label }.
 *   discount_type: 'percent' | 'fixed' | null
 *   discount_value: numeric (percentage or currency amount)
 *   discount_label: optional string shown on the PDF
 *
 * `computeLineTotal(item)` gives the final line total (already discount-adjusted).
 * `computeDocTotals(items, docDiscount)` returns { subtotal, discountAmount, total }
 * where subtotal is the sum of item totals BEFORE doc-level discount, discountAmount
 * is the doc-level discount applied to that subtotal, and total = subtotal - discountAmount.
 */

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const computeItemGross = (item) => {
  const qty = Number(item?.quantity) || 0;
  const price = Number(item?.unit_price) || 0;
  return round2(qty * price);
};

export const computeItemDiscount = (item) => {
  const gross = computeItemGross(item);
  const type = item?.discount_type;
  const value = Number(item?.discount_value) || 0;
  if (!type || value <= 0) return 0;
  if (type === 'percent') return round2(gross * Math.min(value, 100) / 100);
  if (type === 'fixed') return round2(Math.min(value, gross));
  return 0;
};

export const computeItemTotal = (item) => {
  return round2(computeItemGross(item) - computeItemDiscount(item));
};

export const computeDocTotals = (items, docDiscount) => {
  const itemsWithTotals = (items || []).map((it) => ({
    ...it,
    total: computeItemTotal(it),
  }));
  const subtotal = round2(itemsWithTotals.reduce((s, it) => s + (it.total || 0), 0));

  const type = docDiscount?.discount_type;
  const value = Number(docDiscount?.discount_value) || 0;
  let discountAmount = 0;
  if (type && value > 0) {
    if (type === 'percent') discountAmount = round2(subtotal * Math.min(value, 100) / 100);
    else if (type === 'fixed') discountAmount = round2(Math.min(value, subtotal));
  }

  const total = round2(subtotal - discountAmount);
  return { subtotal, discountAmount, total, itemsWithTotals };
};

export const formatDiscountBadge = (item, currency = 'CI$') => {
  const type = item?.discount_type;
  const value = Number(item?.discount_value) || 0;
  if (!type || value <= 0) return null;
  if (type === 'percent') return `${value}% off`;
  return `${currency}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} off`;
};
